import {StepPayload} from '@blesk-frontend/api';
import {createContext, ReactNode, useContext, useEffect, useRef, useState} from 'react';
import {useStore} from 'zustand';
import {useShallow} from 'zustand/shallow';

import {createBlob, cropBlob} from '../libs';
import {createImageEditorStore, ImageEditorState, ImageEditorStore} from '../stores';

const ImageEditorContext = createContext<ImageEditorStore | null>(null);

interface ImageEditorProviderProps {
  children: ReactNode;
  step: StepPayload;
}

export const ImageEditorProvider = ({children, step: _step}: ImageEditorProviderProps) => {
  const [step, setEditorStep] = useState<StepPayload | null>(null);

  useEffect(() => {
    if (!_step) return;
    const load = async () => {
      let blob = await createBlob(_step.src.original);

      const crop = _step.editor.crop || null;
      if (crop) {
        blob = await cropBlob(blob, crop);
      }

      setEditorStep({
        ..._step,
        src: {..._step.src, edited: URL.createObjectURL(blob)},
      });
    };
    load();
  }, [_step]);

  const storeRef = useRef<ImageEditorStore | null>(null);

  if (!storeRef.current || storeRef.current.getState().step !== step) {
    storeRef.current = createImageEditorStore({step});
  }

  return (
    <ImageEditorContext.Provider value={storeRef.current}>{children}</ImageEditorContext.Provider>
  );
};

export function useImageEditorContext<T = ImageEditorState>(
  selector: (state: ImageEditorState) => T,
): T {
  const store = useContext(ImageEditorContext);
  if (!store) throw new Error('Missing ImageEditorContext.Provider in the tree');
  return useStore(store, useShallow(selector));
}
